/**
 * One rule for what counts as an email address, shared by every form and every
 * handler that takes one.
 *
 * An address here is something we send to — an invitation, a payslip, an
 * invoice — so the test is "could this be delivered", not the full RFC 5322
 * grammar, which admits quoted local parts and bare IP hosts that no mail
 * system we hand these to would accept. What it does insist on is the shape
 * typos break: one `@`, a local part without stray dots, and a domain made of
 * real labels ending in an alphabetic top-level domain. "jo@gmail" and
 * "jo@@gmail.com" are the mistakes worth catching before Cognito or a mail
 * server does it for us, after the record is already saved.
 */

/** The longest address a mail path will carry (RFC 5321's 256, less the brackets). */
export const EMAIL_MAX_LENGTH = 254;

/** What a form shows when an address fails {@link isValidEmail}. */
export const INVALID_EMAIL_MESSAGE = "Enter a valid email address, like name@example.com";

const LOCAL_PART = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;
const DOMAIN_LABEL = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/;
const TOP_LEVEL = /^[A-Za-z]{2,63}$/;

/**
 * The address as it identifies a person: trimmed, lowercased, or nothing.
 *
 * Lowercased because the user pool signs in case-insensitively, and an address
 * stored in two casings is two addresses to every comparison that does not.
 */
export function normaliseEmail(value: unknown): string | null {
  const text = String(value ?? "")
    .trim()
    .toLowerCase();
  return text || null;
}

/** Whether `value`, as typed, is an address worth sending to. Surrounding space is ignored. */
export function isValidEmail(value: string): boolean {
  const address = value.trim();
  if (!address || address.length > EMAIL_MAX_LENGTH) return false;

  const at = address.lastIndexOf("@");
  if (at <= 0 || at !== address.indexOf("@")) return false;

  const local = address.slice(0, at);
  const domain = address.slice(at + 1);
  if (local.length > 64 || !LOCAL_PART.test(local)) return false;

  const labels = domain.split(".");
  if (labels.length < 2) return false;
  if (!labels.every((label) => DOMAIN_LABEL.test(label))) return false;
  return TOP_LEVEL.test(labels[labels.length - 1]!);
}
